import { useMemo } from "react";
import { canMutateCloseTemplate } from "@common/helpers/permissions/organizationPermissions";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useUser } from "@hooks/useUser";
import { useActiveOrganizationRole } from "./useActiveOrganizationRole";

/**
 * Returns a function that computes whether the current user is allowed to
 * mutate (edit / delete / archive / apply) a given close template.
 *
 * Permission rules:
 * - Owner / Admin  → can mutate any template
 * - Manager        → can mutate templates they created OR created by a Member
 * - Member         → can only mutate templates they created
 */
export function useCanMutateCloseTemplate(): (
    templateCreatedByUserId: number | null | undefined,
) => boolean {
    const currentUser = useUser();
    const role = useActiveOrganizationRole();
    const { data: members } = useCurrentOrganizationMembers();

    return useMemo(
        () =>
            (templateCreatedByUserId: number | null | undefined): boolean => {
                if (!role || !currentUser) {
                    return false;
                }

                // Resolve the creator's role from the cached members so the
                // shared predicate can apply the Manager branch. While members
                // are still loading this is undefined, which conservatively
                // denies the Manager-on-others-template case until data loads.
                const creator = members?.find(
                    (m) => m.user.id === templateCreatedByUserId,
                );

                return canMutateCloseTemplate({
                    actingUserId: currentUser.id,
                    actingUserRole: role,
                    templateCreatedByUserId,
                    templateCreatorRole: creator?.role ?? null,
                });
            },
        [role, currentUser, members],
    );
}
